<template>
  <div class="row">
    <div class="col-md-3">
      <form class="needs-validation" ref="form" id="form">
        <div class="form-group">
          <!-- part for operatorid -->
          <label class="mb-0" for="Operator">Operator: </label>
          <select class="form-control" v-model="operator" required>
            <option
              v-for="operator in operators"
              :key="operator.id"
              :value="operator.id"
            >
              {{ operator.username }}
            </option>
          </select>
        </div>

        <!-- part for jBoxbatch -->
        <label class="mb-0" for="BatchNumber">Batch Nummer: </label>
        <div class="input-group">
          <input
            type="text"
            class="form-control col-4"
            v-model="batchNrDefault"
            id="addon"
            required
          />
          <input
            type="text"
            class="form-control col"
            v-model="batchNumber"
            id="BatchNumber"
            required
          />
        </div>

        <!-- datePicker for manufacturingdate -->
        <div class="mb-3 form-group">
          <label class="mb-0" for="ManufacturingDate">{{
            datePickerLabel
          }}</label>
          <VueDatePicker
            class="b-form-datepicker"
            v-model="manufacturingDate"
            :enable-time-picker="false"
            required
            >{{ manufacturingDate }}</VueDatePicker
          >
        </div>

        <!-- part for isDiodenApproved -->
        <div class="mb-2 form-check">
          <input
            class="form-check-input"
            type="checkbox"
            v-model="isApproved"
            value=""
            id="diodesApprovment"
          />
          <label class="form-check-label" for="diodesApprovment">
            Diodentyp i.O.? (SB3050DY)
          </label>
        </div>

        <!-- part for jBoxtype -->
        <div class="form-group">
          <label class="mb-2">Dose: </label>
          <div v-for="(dose, id) in doses" :key="id">
            <input
              type="radio"
              :id="id"
              v-model="selectedDose"
              :value="dose"
              class="radio-input"
            />
            <label :for="id">{{ dose }}</label>
          </div>
        </div>

        <button
          type="button"
          class="btn btn-primary mb-3 mt-4"
          @click="submit()"
        >
          Speichern
        </button>
      </form>

      <!-- success alert -->
      <transition name="alert">
        <div
          class="alert alert-success mt-1"
          role="alert"
          v-if="triggerSuccess"
        >
          Der Eintrag wurde gespeichert!
        </div>
      </transition>

      <!-- reminder Fassaden-Modul alert -->
      <transition name="alert">
        <div
          class="alert alert-danger mt-1"
          role="alert"
          v-if="triggerFassadeModul"
        >
          Die Komponenten 31002871, 31002872, 31002873 müssen installiert
          werden!
        </div>
      </transition>

      <!-- reminder GBS-Modul alert -->
      <transition name="alert">
        <div
          class="alert alert-danger mt-1"
          role="alert"
          v-if="triggerGBSModul"
        >
          Die Komponente 31002815 muss installiert werden!
        </div>
      </transition>

      <!-- reminder Balkon-Modul alert -->
      <transition name="alert">
        <div
          class="alert alert-danger mt-1"
          role="alert"
          v-if="triggerBalkonModul"
        >
          Die Komponenten 31004245, 31004246, 31004247 müssen installiert
          werden!
        </div>
      </transition>

      <!-- failed alert -->
      <transition name="alert">
        <div class="alert alert-danger mt-1" role="alert" v-if="triggerError">
          Eintrag konnte nicht gespeichert werden!
        </div>
      </transition>

      <!-- form invalid alert -->
      <transition name="alert">
        <div
          class="alert alert-warning mt-1"
          role="alert"
          v-if="triggerWarning"
        >
          Bitte füllen Sie alle Felder aus!
        </div>
      </transition>

      <!-- Modal abort -->
      <transition name="alert">
        <div class="alert alert-warning mt-1" role="alert" v-if="triggerAbort">
          Vorgang abgebrochen!
        </div>
      </transition>

      <!-- modal component -->
      <transition name="modal">
        <template v-if="showModal">
          <MyModal
            msg="Haben Sie das die ganze Batchnummer überprüft?"
            :visible="showModal"
          >
            <template #success>
              <button
                type="button"
                id="success"
                class="btn btn-success"
                @click="handleModalButton(true)"
              >
                Speichern
              </button>
            </template>
            <template #declined>
              <button
                type="button"
                id="declined"
                class="btn btn-warning"
                @click="handleModalButton(false)"
              >
                Abbrechen
              </button>
            </template>
          </MyModal>
        </template>
      </transition>
    </div>

    <div class="col-md-9">
      <JBoxTable
        :url="props.tableUrl"
        :key="componentKey"
        @updateLatestJBoxBatch="updateJboxBatchFromTable"
      />
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, defineProps, watch } from "vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import JBoxTable from "@/components/jBox/JBoxTable.vue";
import MyModal from "@/components/helpers/MyModal.vue";
import moment from "moment-timezone";
import { instance as axios } from "@/services/axios";

const props = defineProps({
  stationId: {
    type: Number,
    required: true,
  },
  tableUrl: {
    type: String,
    required: true,
  },
});

const componentKey = ref(0);
const manufacturingDate = ref(new Date().toISOString());
const batchNrDefault = ref();
const batchNumber = ref();

let operator = ref();
let doses = ref({
  1: "GBS-Modul",
  2: "Fassaden-Modul",
  3: "Balkonmodul",
});
let selectedDose = ref("");
let datePickerLabel = ref("Herstellerdatum: ");
let isApproved = ref();

const operators = ref([]);

// trigger values for alerts
let triggerSuccess = ref(false);
let triggerError = ref(false);
let triggerWarning = ref(false);
let triggerAbort = ref(false);
let triggerGBSModul = ref(false);
let triggerFassadeModul = ref(false);
let triggerBalkonModul = ref(false);

let showModal = ref(false);
let modalResolve;

watch(selectedDose, (newDose) => {
  if (newDose === "GBS-Modul") {
    triggerGBSModul.value = true;
    setTimeout(() => (triggerGBSModul.value = false), 5000);
  } else if (newDose === "Fassaden-Modul") {
    triggerFassadeModul.value = true;
    setTimeout(() => (triggerFassadeModul.value = false), 5000);
  } else {
    triggerBalkonModul.value = true;
    setTimeout(() => (triggerBalkonModul.value = false), 5000);
  }
});

watch(manufacturingDate, (newValue) => {
  if (newValue instanceof Date) {
    manufacturingDate.value = newValue.toISOString();
  } else if (typeof newValue === "string") {
    manufacturingDate.value = new Date(newValue).toISOString();
  }
});

// watch function for scanner part
watch(batchNumber, (newValue) => {
  if (newValue && newValue.includes("$")) {
    batchNumber.value = newValue.slice(newValue.indexOf("$") + 1);
  }
});
document.addEventListener("keydown", (event) => {
  if (event.key === "$") {
    batchNumber.value = "";
  }
});

onMounted(async () => {
  try {
    const { data } = await axios.get("/User/GetUsersByJobtype/3");
    operators.value = data
      .map(({ id, username }) => ({ id, username }))
      .sort((a, b) => a.username.localeCompare(b.username));
  } catch (error) {
    console.log(error.message);
  }
});

const updateJboxBatchFromTable = (jBoxBatch) => {
  batchNrDefault.value = jBoxBatch;
};

//modal handler. Get called in the submit function
const handleModal = () => {
  showModal.value = true;
  return new Promise((resolve) => {
    modalResolve = resolve;
  });
};

//passes the button press of the modal to modal handler
const handleModalButton = (value) => {
  showModal.value = false;
  modalResolve(value);
};

// reset the form after submit it
function resetForm() {
  operator.value = null;
  batchNumber.value = "";
  manufacturingDate.value = null;
  selectedDose.value = null;
  componentKey.value += 1;
  document.getElementById("diodesApprovment").checked = false;
}

// submit the form for adding a new jbox
async function submit() {
  const currentDate = moment().tz("Europe/Zurich").format();

  if (
    !document.getElementById("form").checkValidity() ||
    isApproved.value == null ||
    manufacturingDate.value == null ||
    operator.value == null ||
    selectedDose.value == null ||
    batchNumber.value == null
  ) {
    //warning alert
    triggerWarning.value = true;
    setTimeout(() => (triggerWarning.value = false), 5000);
    return;
  }

  //ask every time whether the batch number is correct or not
  const modalResult = await handleModal();
  if (!modalResult) {
    //abort alert
    triggerAbort.value = true;
    setTimeout(() => (triggerAbort.value = false), 5000);
    return;
  }

  // json object for the POST-Request
  let jsonObject = {
    operatorid: operator.value,
    stationid: 35,
    dateInProduction: currentDate,
    jBoxtype: selectedDose.value,
    jBoxbatch: batchNrDefault.value + batchNumber.value,
    manufacturingdate: manufacturingDate.value,
    isDiodesApproved: isApproved.value,
  };

  try {
    const response = await axios.post("/Material/AddJBox", jsonObject);
    console.log("Erfolgreiche Antwort:", response.data);
    resetForm();
  } catch (e) {
    console.log("Ein Fehler ist aufgetreten:", e.message);
    setTimeout(() => (triggerError.value = false), 5000);
    resetForm();
  }
  triggerSuccess.value = true;
  setTimeout(() => (triggerSuccess.value = false), 5000);
}
</script>

<style>
.alert-enter-active,
.alert-leave-active,
.modal-enter-active {
  transition: opacity 0.5s ease;
}
.alert-enter-from,
.alert-leave-to,
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

input[type="radio"] {
  transform: scale(1.3);
}
input[type="checkbox"] {
  transform: scale(1.2);
}
.radio-input {
  margin-right: 10px; /* Abstand zwischen dem Radio-Button und dem Label */
}
</style>
